/**
 * Created by SH: 02/20/2020
 */

import { TweenLite, Power2, Timeline, TimelineLite } from "gsap";

import { PunkSymbol } from "./PunkSymbol";
import { ReelEngineController, ReelState } from "@nolimitcity/slot-game/bin/core/engines/reel/ReelEngineController";
import { ReelSymbolStates } from "@nolimitcity/slot-game/bin/core/engines/reel/symbol/ReelSpriteSymbolArea";
import { ReelSymbolSize, PunkSymbolData } from "./config/PunkSymbolData";
import { PunkEnginePlayer, IExpandAnimateConfig, IExpandingConfig } from "./PunkEnginePlayer";
import { PunkParsedInitData } from "../server/data/PunkParsedInitData";
import { PunkParsedGameData } from "../server/data/PunkParsedGameData";
import { GameEvent } from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import { ReelEngineEvent } from "@nolimitcity/slot-game/bin/core/engines/reel/event/ReelEngineEvent";
import { EventHandler } from "@nolimitcity/slot-game/bin/core/event/EventHandler";

export class PunkEngineController extends ReelEngineController {

    protected _graphicBg: PIXI.Graphics;
    protected _engine: PunkEnginePlayer;

    public get AllStickySymbols(): PunkSymbol[] {
        return this._stickyContainer.children as PunkSymbol[];
    }

    public get AllSymbols(): PunkSymbol[] {
        return this._areaContainer.children as PunkSymbol[];
    }

    public get statePosOffset(): number {
        return this.state.isIdle ? 0 : this.Engine.reverse ? 1 : -1;
    }

    public get AllWindowSymbols(): PunkSymbol[] {
        return this.AllSymbols.filter(sym => { return (sym.posID >= 0 && sym.posID < this.windowAmount) });
    }

    public get topSymbol(): PunkSymbol {
        return this.AllSymbols[0];
    }

    public get bottomSymbol(): PunkSymbol {
        return this.AllSymbols[this.AllSymbols.length - 1];
    }

    public getSymbol(symbolID: number): PunkSymbol | undefined {
        return super.getSymbol(symbolID) as PunkSymbol;
    }

    public get reelGraphicBg(): PIXI.Graphics {
        return this._graphicBg;
    }

    public get windowTopSymbol(): PunkSymbol {
        return this.AllSymbols[this.Engine.reverse ? this.overflowTail : this.overflowDirection]
    }

    public get windowBottSymbol(): PunkSymbol {
        return this.AllSymbols[(this.AllSymbols.length - 1) - (this.Engine.reverse ? this.overflowDirection : this.overflowTail)];
    }
    
    protected addChildObjOrder(): void {
        this._graphicBg = this.createGraphic();
        this._graphicBg.alpha = 0;
        super.addChildObjOrder();
    }

    public createStart(reverse: boolean = false): Timeline {
        this.Engine.reverse = reverse;
        this.isQuickStop = false;
        if (this.isActive) {
            return this.createNextAnimation(ReelState.START);
        }
        else if (this.visible) {
            return this.createNextAnimation(ReelState.START_TEASER);
        }
        else {
            return this.createNextAnimation(ReelState.INACTIVE, true);
        }
    }

    public createNextAnimation(state: string, updateState: boolean = false): Timeline {
        switch (state) {
            case ReelState.START_TEASER:
                this._engine.StartTeaser();
                break;
            case ReelState.STOP_TEASER:
                this._engine.StopTeaser(this.isFast);
                break;
            default:
                return super.createNextAnimation(state, updateState);
        }
        while (!this._engine.nextTimeline) this.createNextAnimation(state, updateState);
        return this._engine.nextTimeline;
    }


    public dataParsed(data: PunkParsedInitData | PunkParsedGameData): void {
        this._stopReelSet = Array.from(data.reels[this.reelID]);
        this._quickStopReelSet = this.defineQuickReel(data);

        this._nextReelSetName = data.nextReelSetName;
        this._stickies = this.getStickyGameData(data);
        this._nextResultActive = this.getActiveReels(data);

    };

    protected defineQuickReel(data: PunkParsedInitData | PunkParsedGameData): string[] {
        const parsedGData: PunkParsedGameData = (data as PunkParsedGameData);
        if (parsedGData.wildLandPositions == undefined) return data.reels[this.reelID];
        const wildPos: number = parsedGData.wildLandPositions[this.reelID];
        if (wildPos == -1) return data.reels[this.reelID];

        let qReels: string[] = Array.from(data.reels[this.reelID]);
        for (let row: number = wildPos; row >= 1; row--) qReels[row] = "W";

        return qReels;
    }

    protected getActiveReels(data: PunkParsedInitData | PunkParsedGameData): boolean {
        if (data.scatterPositions && data.respinTriggered && this.reelID != 5) {
            return data.scatterPositions![this.reelID] == -1 && (data! as PunkParsedGameData).wildLandPositions![this.reelID] == -1;
        }
        else return true;
    }

    protected createNewSymbol(id: number, type: string, state?: string, size?: number): PunkSymbol {
        return new PunkSymbol(id, type, state, size);
    }

    protected createReelEngine(): void {
        this._engine = new PunkEnginePlayer(this.engineCallbacks(), this.config.engine_config, this._areaContainer);
    }

    public createStop(isTeasing?: boolean): Timeline {
        if (this.state.isSpinning && !this.Engine.nextTimeline) {
            this.Engine.Steps = (this.showAmount - this.engineConfig.WINDOW_AMOUNT) + (this._stopReelSet.length - this.overflowDirection - (this.Engine.reverse ? 2 : 0));
            return this.createNextAnimation(isTeasing ? ReelState.STOP_TEASER : ReelState.STOP);
        }
        return new TimelineLite();
    }

    protected createRandomSymbol(): PunkSymbol {
        return this.createNewSymbol(this.children.length, this.generateNextSymbol(this._currentReelSet));
    }

    protected onStopStarted(): void {
        super.onStopStarted();
    }

    public stopVisibleCompleted():void{
        if(this.visible) {
            this._activeReel = this._nextResultActive;
        }
    }
    
    protected onStopAnimationCompleted():void{
        // console.log(this._stopReelSet)
        super.onStopAnimationCompleted();
    }

    public startReel(): void {
        if (this.state.isSpinning) return;

        this.changeReelAmount(3);

        super.startReel();
    }

    public get overflowDirection(): number {
        return (this.Engine.reverse ? this.engineConfig.OVERFLOW_BOTT : this.engineConfig.OVERFLOW_TOP) + this.engineConfig.RESULT_OVERFLOW;
    }

    public get totalOverflow(): number {
        return this.engineConfig.OVERFLOW_TOP + this.engineConfig.OVERFLOW_BOTT + this.engineConfig.RESULT_OVERFLOW;
    }

    public changeReelAmount(newSize: number): void {
        this._symbolAmount = this._showAmount = newSize;
        const smallerSize: number = (ReelSymbolSize.SEVEN) - newSize;

        this.Engine.stepDistance = [360, 324, 270, 180, 135, 108, 90][newSize];
        let absStep: number = Math.abs(this.Engine.stepDistance);

        for (let i = 0; i < this.AllSymbols.length; i++) {
            if (this.AllSymbols[i].posID < 0 || this.AllSymbols[i].posID > this.windowBottSymbol.posID) {
                const sym: PunkSymbol = this.AllSymbols[i];
                sym.changeState({
                    state: ReelSymbolStates.CHANGE_SYMBOL,
                    forceState: true,
                    type: smallerSize,
                });
                sym.changeSizeBg(0);
                sym.position.y = (absStep - (this.Engine.defaultStep)) * 0.5 + (sym.symbolHeight + this.engineConfig.STEP_DISTANCE_PADDING_Y) * sym.posID;
            }
        }
        if (this.state.isSpinning) {
            this.updateStep();
        }
    }

    public onExpandSymbol(data: IExpandAnimateConfig): void {

        let targetSym: number | undefined = data.symbolID != undefined ? data.symbolID : undefined;
        let newSymbolSize: number = data.toSize - this._showAmount;
        let affectedSymbols: number = this._showAmount;
        const smallerSize: number = (ReelSymbolSize.SEVEN + 1) - data.toSize;
        if ((ReelSymbolSize.SEVEN - smallerSize) <= this._showAmount) return;

        if (data.duration == undefined) data.duration = this.engineConfig.DURATION.EXPAND;

        if (targetSym != undefined && !this.getSymbol(targetSym)!.symbolConfig.MULTISIZE && !data.skipSplit) {
            var symbolToCreate: PunkSymbol = this.getSymbol(targetSym)!;
            let amountOfSymbols: number = newSymbolSize;
            let startID: number = symbolToCreate.posID;
            if (amountOfSymbols > 1) symbolToCreate.stateType.set(smallerSize);

            for (let i = 0; i < amountOfSymbols - 1; i++) {
                let freeSymbol: PunkSymbol | undefined;
                freeSymbol = this.createNewSymbol(500, symbolToCreate.symbolConfig.NAME);
                freeSymbol.stateType.set(smallerSize);
                freeSymbol.posID = startID + i;

                for (let sId = 0; sId < this.AllSymbols.length; sId++) {
                    if (this.AllSymbols[sId].posID >= startID + i)
                        this.AllSymbols[sId].posID++;
                }

                freeSymbol.position.y = symbolToCreate.position.y;
                this.addSymbol(freeSymbol);
                affectedSymbols++;
                newSymbolSize--;
            }
            targetSym = undefined;
        }

        this._symbolAmount = affectedSymbols;

        const engineExpandConfig: IExpandingConfig = {
            ease: data.ease,
            targetSym: targetSym,
            duration: data.duration,
            toSize: newSymbolSize,
            startSize: this._showAmount,
            amountOfSymbols: this._symbolAmount,
            updateSize: data.updateSize,
            addOverflow: data.addOverflow,
        }

        const haveTarget: boolean = targetSym != undefined;

        if (haveTarget) {
            this.getSymbol(targetSym!)!.changeState({
                state: ReelSymbolStates.CHANGE_SYMBOL,
                forceState: true,
                type: data.toSize,
                duration: data.duration! * 0.9,
            });
        }

        if (data.addOverflow) {
            for (let i = 0; i < this.AllSymbols.length; i++) {
                if (haveTarget && i == targetSym) continue;
                this.AllSymbols[i].changeState({
                    state: ReelSymbolStates.CHANGE_SYMBOL,
                    forceState: true,
                    type: smallerSize,
                    duration: data.duration! * 0.9,
                });
            }
        } else {
            for (let pos = 0; pos < this.AllSymbols.length; pos++) {
                if (this.AllSymbols[pos].posID < 0 || this.AllSymbols[pos].posID > this.windowBottSymbol.posID) continue;
                if (haveTarget && this.AllSymbols[pos].posID == targetSym) continue;
                this.getSymbol(this.AllSymbols[pos].posID)!.changeState({
                    state: ReelSymbolStates.CHANGE_SYMBOL,
                    forceState: true,
                    type: smallerSize,
                    duration: data.duration! * 0.9,
                });
            }
        }

        if (data.updateSize) this._showAmount = ReelSymbolSize.SEVEN - smallerSize;

        this._engine.ExpandSymbol(engineExpandConfig);
        while (!this._engine.nextTimeline) this._engine.ExpandSymbol(engineExpandConfig);
        this.state.set(ReelState.EXPAND_SYM);
        this._engine.activateNextAnimation();
    }

    protected addingNextStepSymbol(_posID: number, _sym: string, _state: string): PunkSymbol {
        const type: number = (ReelSymbolSize.SEVEN) - this._showAmount;
        return this.createNewSymbol(_posID, _sym, _state, type);
    }

    protected onStarting(): void {
        super.onStarting();
        TweenLite.to(this.reelGraphicBg, 0.2, { alpha: 1, ease: this.engineConfig.EASING.START });
    }

    protected createGraphic(): PIXI.Graphics {
        const graphicBg: PIXI.Graphics = new PIXI.Graphics();
        graphicBg.beginFill(0x000000, 1);
        graphicBg.drawRect(-PunkSymbolData.DEFAULT_SYMBOL.DIM.WIDTH * 0.5, -PunkSymbolData.DEFAULT_SYMBOL.DIM.HEIGHT * 0.5,
            PunkSymbolData.DEFAULT_SYMBOL.DIM.WIDTH, this.reelDim.HEIGHT);
        graphicBg.endFill();
        this.addChild(graphicBg);
        return graphicBg;
    }

    protected onBounceStarted(): void {
        TweenLite.to(this.reelGraphicBg, 0.1, { alpha: 0, ease: Power2.easeOut });
        super.onBounceStarted();
    }

    protected completedExpanding(): Timeline {
        // Hide symbols. Using amount size due that the amount of symbols is not the same as the amount of hitPositions=ShowPositions.
        for (let posID: number = -this.overflowDirection; posID < this._symbolAmount + this.overflowTail; posID++) {
            if (posID == 0) posID = this._symbolAmount;
            if (this.getSymbol(posID) !== undefined && (posID < 0 || posID >= this._symbolAmount)) this.getSymbol(posID)!.visible = false;
        }

        if (this._showAmount >= this._symbolAmount)
            this.changeReelAmount(this._showAmount);

        this.state.set(ReelState.IDLE);
        return super.createNextAnimation(ReelState.IDLE);
    }
}